import React, { useEffect, useMemo, useState } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import styles from './styles.module.scss';

type Props = {
  open: boolean;
  onClose: () => void;
  images?: string[];
  loading?: boolean;
};

const InsuranceCardModal = ({ open, onClose, images = [], loading }: Props) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (open) {
      setCurrentIndex(0);
    }
  }, [open]);

  const hasMultipleImages = images.length > 1;

  const currentImage = useMemo(() => {
    if (!images.length) return null;
    return images[currentIndex] || images[0];
  }, [images, currentIndex]);

  const handlePrev = (): void => {
    if (!images.length) return;

    setCurrentIndex((prev) => {
      if (prev === 0) return images.length - 1;
      return prev - 1;
    });
  };

  const handleNext = (): void => {
    if (!images.length) return;

    setCurrentIndex((prev) => {
      if (prev === images.length - 1) return 0;
      return prev + 1;
    });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.overlay}>
        <Box className={styles.modal}>
          <div className={styles.header}>
            <span className={styles.title}>Insurance card</span>

            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </div>

          <div className={styles.body}>
            {loading ? (
              <div className={styles.loader}>
                <CircularProgress />
              </div>
            ) : currentImage ? (
              <div className={styles.imageWrapper}>
                {hasMultipleImages && (
                  <button
                    type="button"
                    className={`${styles.navButton} ${styles.navLeft}`}
                    onClick={handlePrev}
                    aria-label="Previous insurance card image"
                  >
                    <ChevronLeftIcon />
                  </button>
                )}

                <img src={currentImage} alt={`Insurance card ${currentIndex + 1}`} className={styles.image} />

                {hasMultipleImages && (
                  <button
                    type="button"
                    className={`${styles.navButton} ${styles.navRight}`}
                    onClick={handleNext}
                    aria-label="Next insurance card image"
                  >
                    <ChevronRightIcon />
                  </button>
                )}
              </div>
            ) : (
              <div className={styles.empty}>No insurance card available</div>
            )}
          </div>
        </Box>
      </Box>
    </Modal>
  );
};

export default InsuranceCardModal;